import React, { useEffect, useRef, useState } from "react";
import { createModal } from "../hooks/createModal";
import { ROUTES } from "../utils/Routes";
import { KBD } from "../utils/Keybindings";
import { listen } from "../hooks/Listener";
import { pubCall } from "../utils/PubSub";
import { showError } from "../ui/Message";
import Tabs from "../ui/Tabs";
import Html from "../utils/Html";
import dayjs from "dayjs";
import Divider from "../ui/Divider";
import Space from "../ui/Space";
import Spin from "../ui/Spin";
import { apiCall } from "../utils/Api";
import Label, { DisplayLabel } from "../ui/Label";

export const renderMarketing = (data = {}) => {
    const {
        key_points = [],
        more_info = [],
        awards = [],
        promotion = [],
        printing = [],
        sales_rights = [],
        extras = {}
    } = data;

    const {
        links = [],
        publisher_links = [],
        author_links = [],
        video_links = []
    } = extras || {};

    const renderList = (title, items = []) => {
        if (!Array.isArray(items) || items.length === 0) return null;

        return (
            <div style={{ marginBottom: 16 }}>
                <strong style={{ display: "block", marginBottom: 6 }}>
                    {title}
                </strong>

                <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {items.map((item, index) => (
                        <li key={`${title}-${index}`}>
                            {typeof item === "object" ? item.label || item.title || item.name || item.url : item}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const renderLinks = (title, items = []) => {
        if (!Array.isArray(items) || items.length === 0) return null;

        return (
            <div style={{ marginBottom: 16 }}>
                <strong style={{ display: "block", marginBottom: 6 }}>
                    {title}
                </strong>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }} >
                    {items.map((item, index) => {
                        const url = typeof item === "string" ? item : item.url || item.href;
                        const label = typeof item === "string" ? item : item.label || item.title || item.name || url;

                        if (!url) return null;

                        return (
                            <a key={`${title}-${index}`} href={url} target="_blank" >
                                {label}
                            </a>
                        );
                    })}
                </div>
            </div>
        );
    };

    const hasContent =
        key_points.length > 0 ||
        more_info.length > 0 ||
        awards.length > 0 ||
        promotion.length > 0 ||
        printing.length > 0 ||
        sales_rights.length > 0 ||
        links.length > 0 ||
        publisher_links.length > 0 ||
        author_links.length > 0 ||
        video_links.length > 0;

    if (!hasContent) {
        return <div></div>;
    }

    return (
        <div style={{ "fontSize": "16px" }}>
            {renderList("Key Points", key_points)}

            {more_info.map((html, index) => (
                <div
                    key={`more-info-${index}`}
                    style={{ marginBottom: 16, lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            ))}

            {renderList("Awards", awards)}
            {renderList("Promotion", promotion)}
            {renderList("Printing", printing)}
            {renderList("Sales Rights", sales_rights)}
            {renderLinks("Links", links)}
            {renderLinks("Publisher Links", publisher_links)}
            {renderLinks("Author Links", author_links)}
            {renderLinks("Videos", video_links)}
        </div>
    );
};


export const renderReviews = (reviews = []) => {
    if (!Array.isArray(reviews) || reviews.length === 0) {
        return <div>No reviews available.</div>;
    }

    return (
        <div>
            {reviews.map(review => (
                <div key={review.id}>
                    <div className="ui-comment-text">
                        <Html html={review.content} />
                    </div>
                    <div className="shim" /><div className="shim" />
                    <Divider />
                </div>
            ))}
        </div>
    );
};


export const renderComments = (comments = []) => {
    if (!Array.isArray(comments) || comments.length === 0) {
        return <div>No comments available.</div>;
    }

    return (
        <div className="ui-comments">
            {comments.map(comment => (
                <div key={comment.id}>
                    <div className="ui-comment-header">
                        <strong>{comment.author || "Unknown author"}</strong>

                        {comment.date && (
                            <span className="ui-comment-date">
                                {dayjs(comment.date * 1000).format("MMM D, YYYY")}
                            </span>
                        )}
                    </div>

                    <div className="ui-comment-text">
                        <Html html={comment.text} />
                    </div>
                    <div className="shim" /><div className="shim" />
                    <Divider />
                </div>
            ))}
        </div>
    );
};


export const renderAuthors = (authors) => {
    if (!Array.isArray(authors) || authors.length === 0) {
        return <div>No author.</div>;
    }

    return (
        <Space>
            {authors.map(author => {
                return (
                    <div>
                        {author?.name || ""}
                    </div>
                )
            })}
        </Space>
    )

}

export const renderBios = (bios = []) => {
    if (!Array.isArray(bios) || bios.length === 0) {
        return <div>No bio.</div>;
    }

    return (
        <div className="ui-comments">
            {bios.map(bio => (
                <div key={bio.id}>
                    <Html html={bio} />
                    <div className="shim" /><div className="shim" />
                    <Divider />
                </div>
            ))}
        </div>
    );
};


export const ExpandedCoverImage = (props) => {
    const {
        controller = "",
        route = "",
        path = "",
        cover_key = "",
        placeholder = "",
        primary_code = "",
        callback = () => { }
    } = props;

    const [titleInfo, setTitleInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [state, setState] = useState({ view: "images", imageIndex: 0 });
    const views = ["images", "description", "comments", "author", "reviews", "marketing"];
    const thumbnailRefs = useRef([]);
    const thumbnailContainerRef = useRef(null);
    const contentRefs = useRef({});



    const TitleSummary = ({ data = {} }) => {

        const {
            title = "",
            subtitle = "",
            binding = "",
            list_price_can = "",
            list_price_usa = "",
            release_date = 0,
            rank = {},
            authors = [],
            publisher = "",
            subjects = [],
            pages = 0,
            width = 0,
            height = 0,
            weight = 0,
            carton_qty = 0
        } = data;

        const fullTitle = [title, subtitle]
            .filter(Boolean)
            .join(": ");

        const subjectString = subjects
            .map(subject => {
                const subs = subject.sub_labels
                    ?.map(item => item.label)
                    .filter(Boolean)
                    .join(" / ");

                return [subject.subject, subs]
                    .filter(Boolean)
                    .join(" / ");
            })
            .join(" / ");

        const details = [
            pages && `${pages} pages`,
            width && height && `${width}" x ${height}"`,
            weight && `${weight}lbs`,
            carton_qty && `Crtn: ${carton_qty}`
        ].filter(Boolean).join(" | ");

        return (
            <div>



                <Space>
                    <Label style={{"textAlign" : "left"}} width={90}>Binding</Label>
                    <DisplayLabel>{binding}</DisplayLabel>
                </Space>

                {((list_price_can > 0) &&
                    <Space>
                        <Label style={{"textAlign" : "left"}}  width={90}>List price CA</Label>
                        {(list_price_can &&
                            <DisplayLabel>${list_price_can} CAD</DisplayLabel>
                        )}

                    </Space>
                )}
                {((list_price_usa > 0) &&
                    <Space>
                        <Label style={{"textAlign" : "left"}}  width={90}>List price US</Label>
                        {(list_price_usa &&
                            <DisplayLabel>${list_price_usa} CAD</DisplayLabel>
                        )}
                    </Space>
                )}

                {((release_date > 0) &&
                    <Space>
                        <Label style={{"textAlign" : "left"}}  width={90}>Release date</Label>
                        <DisplayLabel> {dayjs.unix(release_date).format("MMM D, YYYY")}</DisplayLabel>
                    </Space>
                )}

                {((rank?.bestseller > 0) &&
                    <Space>
                        <Label style={{"textAlign" : "left"}}  width={90}>Rank</Label>
                        <DisplayLabel> #{rank.bestseller} in bestsellers</DisplayLabel>
                    </Space>
                )}

                {((authors?.length) &&
                    <Space>
                        <Label style={{"textAlign" : "left"}}  width={90}>Author(s)</Label>
                        <DisplayLabel>{authors.map(item => item.name).filter(Boolean).join(", ")}</DisplayLabel>
                    </Space>
                )}

                {(publisher &&
                    <Space>
                        <Label style={{"textAlign" : "left"}}  width={90}>Publisher</Label>
                        <DisplayLabel>{publisher}</DisplayLabel>
                    </Space>
                )}

                {(subjectString &&
                    <Space>
                        <Label style={{"textAlign" : "left"}}  width={90}>Subjects</Label>
                        <DisplayLabel>{subjectString}</DisplayLabel>
                    </Space>
                )}

                 {(details &&
                    <Space>
                        <Label style={{"textAlign" : "left"}}  width={90}>Details</Label>
                        <DisplayLabel>{details}</DisplayLabel>
                    </Space>
                )}

            </div>
        );
    }

    useEffect(() => {
        thumbnailRefs.current[state.imageIndex]?.focus();
    }, [state.imageIndex]);

    const images = [
        { key: "cover", src: path || placeholder },
        ...(titleInfo?.interior_objects || []).map(item => ({
            key: item.key,
            // src: `https://cdn1.bookmanager.com/i/m?b=${titleInfo.eisbn}&imgp=${item.key}&cb=${item.cb}`,
            src: `https://bookmanager.com/i/bmo/bisbn/${cover_key}/resize/700/imgp/${item.key}/cb/${item.cb}`,

        }))
    ];

    (titleInfo?.interior_objects || []).map(item => {

        console.log("image data", item)
        console.log(`https://bookmanager.com/i/bmo/bisbn/${cover_key}/resize/700/imgp/${item.key}/cb/${item.cb}`)
    })

    const handleVerticalArrow = (_dir) => {
        if (state.view === "images") {
            changeImage(_dir);
            return;
        }

        contentRefs.current[state.view]?.scrollBy({
            top: _dir * 60,
            behavior: "smooth"
        });
    };

    const changeImage = (_dir) => {

        setState(prev => {
            let next = prev.imageIndex + _dir;
            if (next > images.length - 1) { next = 0 }
            if (next < 0) { next = images.length - 1; }
            console.log("Next is", next)
            return { ...prev, imageIndex: next }
        })
    };

    const getTitleInfo = () => {
        apiCall("title/getItem", { isbn: primary_code }, (_s, _r) => {
            if (_s) {
                setTitleInfo(_r);
            } else {
                showError(_r);
            }
            setLoading(false)
        })
    };

    const changeView = (_dir = 1) => {
        setState(prev => {
            let _current = views.indexOf(prev.view);
            let _next = _current + _dir;
            if (_next < 0) { _next = views.length - 1; }
            if (_next >= views.length) { _next = 0; }
            return { ...prev, view: views[_next] };
        });
    };

    useEffect(() => {
        if (primary_code) {
            getTitleInfo();
        }
    }, [primary_code]);

    useEffect(() => {
        const actions = [
            { key: KBD.ESCAPE, action: callback },
            { key: KBD.SPACE, action: callback },
            { key: KBD.ARROW_LEFT, action: () => changeView(-1) },
            { key: KBD.ARROW_RIGHT, action: () => changeView(1) },
            { key: KBD.ARROW_UP, action: () => handleVerticalArrow(-1) },
            { key: KBD.ARROW_DOWN, action: () => handleVerticalArrow(1) }
        ];

        return listen(controller, route, actions, () => { }, [KBD.ESCAPE, KBD.SPACE, KBD.ARROW_LEFT, KBD.ARROW_RIGHT, KBD.ARROW_DOWN, KBD.ARROW_UP]);
    }, [controller, route, callback, images, state.view]);

    const handleImageError = (e) => {
        const img = e.currentTarget;
        if (!placeholder || img.src === placeholder) { return; }
        img.onerror = null;
        img.src = placeholder;
    };


    const drawCover = () => (
        <div
            style={{
                width: "100%",
                height: "calc(100vh - 240px)",
                display: "flex",
                gap: 12,
                minWidth: 0,
                overflow: "hidden",
            }}
        >
            <div
                ref={thumbnailContainerRef}
                style={{
                    width: 100,
                    height: "100%",
                    flexShrink: 0,
                    display: "flex",
                    backgroundColor: "var(--site-background)",
                    padding: "10px 10px",
                    flexDirection: "column",
                    gap: 8,
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >

                {images.map((image, index) => (
                    <div
                        key={image.key}
                        tabIndex={-1}
                        ref={el => thumbnailRefs.current[index] = el}
                        onClick={() => {
                            setState(prev => ({ ...prev, imageIndex: index }));
                        }}
                        style={{
                            width: "100%",
                            flexShrink: 0,
                            outline: "none",
                            padding: 3,
                            border: index === state.imageIndex
                                ? "2px solid var(--text-blue)"
                                : "2px solid var(--border-primary)",
                            cursor: "pointer",
                            boxSizing: "border-box",
                        }}
                    >
                        <img
                            src={image.src}
                            alt=""
                            onError={handleImageError}
                            style={{
                                display: "block",
                                width: "100%",
                                height: "auto",
                                objectFit: "contain",
                            }}
                        />
                    </div>
                ))}
            </div>

            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    width: "680px",
                }}
            >
                <img
                    src={images[state.imageIndex]?.src || placeholder}
                    alt={titleInfo?.title || "Book cover"}
                    onError={handleImageError}
                    style={{
                        display: "block",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                    }}
                />
            </div>
        </div>
    );

    const drawView = (_view, _content) => (
        <div

            style={{
                display: "flex",
                gap: 24,
                width: "100%",
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
                alignItems: "flex-start"
            }}
        >
            <div
                ref={el => contentRefs.current[_view] = el}
                style={{
                    flex: 1,
                    minWidth: 0,
                    height: "100%",
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden"
                }}
            >
                {_content}
            </div>
        </div>
    );

    const drawDescription = () => drawView("description",
        <div>
            <h2 style={{ marginTop: 0,  marginBottom: 0  }}> {titleInfo?.title || ""} </h2>
            <div className="shim" />
            <Divider />
            <div className="shim" />
            <TitleSummary data={titleInfo} />
            
            <div className="shim" /> <div className="shim" />
            <Divider />
            <div className="shim" /> <div className="shim" />
            <div>
                <Html style={{ "fontSize": "16px" }} html={titleInfo?.tab_data?.description || "No description available."} />
            </div>
        </div>
    );

    const drawComments = () => drawView("comments",
        <div>
            <h2 style={{ marginTop: 0 }}>Comments</h2>

            <div style={{ "fontSize": "16px" }}>
                {renderComments(titleInfo?.tab_data?.comments)}
            </div>
        </div>
    );

    const drawAuthor = () => drawView("author",
        <div>
            <h2 style={{ marginTop: 0 }}>
                {renderAuthors(titleInfo?.authors)}
            </h2>

            <div style={{ "fontSize": "16px" }}>
                {renderBios(titleInfo?.tab_data?.bios) || "No author information available."}
            </div>
        </div>
    );

    const drawReviews = () => drawView("reviews",
        <div>
            <h2 style={{ marginTop: 0 }}>Reviews</h2>
            <div style={{ "fontSize": "16px" }}>
                {renderReviews(titleInfo?.tab_data?.reviews) || "No reviews available."}
            </div>
        </div>
    );

    const drawMarketing = () => drawView("marketing",
        <div>
            <h2 style={{ marginTop: 0 }}>Marketing</h2>
            <div style={{ "fontSize": "16px" }}>
                {renderMarketing(titleInfo?.tab_data?.marketing)}

            </div>
        </div>
    )

    const drawImage = () => (
        <div
            style={{
                width: "100%",
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
            }}
        >
            {drawCover()}
        </div>
    );

    const tabData = [
        {
            id: "images",
            label: "Images",
            content: drawImage()
        },
        {
            id: "description",
            label: "Description",
            content: (loading) ? "" : drawDescription()
        },
        {
            id: "comments",
            label: "Comments",
            content: (loading) ? "" : drawComments()
        },
        {
            id: "author",
            label: "Author",
            content: (loading) ? "" : drawAuthor()
        },
        {
            id: "reviews",
            label: "Reviews",
            content: (loading) ? "" : drawReviews()
        },
        {
            id: "marketing",
            label: "Marketing",
            content: (loading) ? "" : drawMarketing()
        }
    ];

    return (
        <>
            <div className="shim" />
            <div className="shim" />

            <div style={{ width: "800px", height: "calc(100vh - 200px)", minHeight: 0, overflow: "hidden" }} >
                <Spin loading={(loading && state?.view !== "images")}>
                    <Space gap={20} style={{ "alignItems": "flex-start" }}>

                        <Tabs
                            br={<div><div className="shim" /><div className="shim" /></div>}
                            selected={state.view}
                            onChange={(view) => {
                                setState(prev => ({ ...prev, view }));
                            }}
                            height="calc(100vh - 250px)"
                            tabData={tabData}
                        />
                    </Space>
                </Spin>

            </div>
        </>
    );
};

export default function CoverImage(props) {

    const { primary_code = "", secondary_code = "", viewFullImage = false, setViewFullImage = () => { }, controller = "", cover_key = "" } = props;

    const coverRef = useRef(null);
    const current = useRef(null);
    const activeImageRef = useRef("");
    const primary = `https://bookmanager.com/i/bkmngr_cover/isbn/${primary_code}`;
    const primaryFailed = useRef(false);
    const bisbn = `https://bookmanager.com/i/bmo/bisbn/${cover_key}/resize/700`;
    const placeholder = "https://bookmanager.com/i/nocover-120.png";

    current.current = primary;
    useEffect(() => {
        activeImageRef.current = primary;

        if (coverRef.current) {
            coverRef.current.style.backgroundImage = `url('${primary}')`;
        }
    }, [primary]);

    useEffect(() => {
        if (cover_key && primaryFailed.current && coverRef.current) {
            primaryFailed.current = false;
            activeImageRef.current = bisbn;
            coverRef.current.style.backgroundImage = `url('${bisbn}')`;
        }
    }, [cover_key]);


    const expandImage = () => {
        const returnRoute = window.bm_state.value;
        window.bm_state.value = ROUTES.HELPERS.COVER;
        const image = activeImageRef.current || primary;
        createModal({
            offset: [0, 0],
            mask_close: true,
            align: "center",
            title: "Titlelink Details",
            component: <ExpandedCoverImage cover_key={cover_key || ""} primary_code={primary_code} handleImageError={handleImageError} placeholder={placeholder} controller={controller} route={ROUTES.HELPERS.COVER} path={image} />,
            closable: true,
            callback: () => {
                window.bm_state.value = returnRoute;
                setViewFullImage(false)
            },
        });
    };

    useEffect(() => {
        if (viewFullImage) {
            expandImage();
        }
    }, [viewFullImage]);

    // const handleImageError = (e) => {
    //     const img = e.currentTarget;
    //     img.onerror = null;
    //     img.src = placeholder;
    //     activeImageRef.current = placeholder;
    //     if (coverRef.current) {
    //         coverRef.current.style.backgroundImage = `url('${placeholder}')`;
    //     }
    // };

    const handleImageError = (e, source) => {

        if (source !== current.current) {
            return;
        }

        const img = e.currentTarget;
        if (!cover_key) {
            primaryFailed.current = true;
            return;
        }

        if (img.src !== bisbn) {
            img.src = bisbn;
            activeImageRef.current = bisbn;

            if (coverRef.current) {
                coverRef.current.style.backgroundImage = `url('${bisbn}')`;
            }

            return;
        }

        img.onerror = null;
        img.src = placeholder;
        activeImageRef.current = placeholder;

        if (coverRef.current) {
            coverRef.current.style.backgroundImage = `url('${placeholder}')`;
        }
    };



    return (

        <div
            key={primary_code}
            ref={coverRef}
            className="img-contain fade"
            id="cover-div"
            onClick={() => setViewFullImage(true)}
            style={{
                height: "255px",
                backgroundPosition: "top center",
                backgroundSize: "contain",
                cursor: "pointer",
                backgroundImage: `url('${primary}')`,
            }}
        >

            <img
                src={primary}
                alt="Cover"
                style={{ visibility: "hidden" }}
                onError={(e) => handleImageError(e, primary)}
            />
        </div>

    );
}